import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Alert,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Policy as PolicyIcon,
  Computer as WindowsIcon,
  Apple as AppleIcon,
  Android as AndroidIcon,
  PhoneIphone as IOSIcon,
  Search as SearchIcon,
  Sync as SyncIcon,
  CompareArrows as CompareIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  ExpandMore as ExpandMoreIcon,
  DoneAll as SelectAllIcon,
  Clear as ClearSelectionIcon
} from '@mui/icons-material';
import api from '../api';

interface PolicyData {
  CustomerPolicyID: number;
  CustomerID: number;
  CompanyName: string;
  PolicyID: number;
  PolicyName: string;
  DisplayName: string;
  Platform: string;
  PolicyType: string;
  SecurityLevel: string;
  OwnershipType: string;
  DeploymentStatus: string;
  DeployedDate: string;
  LastModifiedDate: string;
  PolicyObjectID: string;
  IsCustomized: boolean;
  DeploymentID: number;
  LicenseScenario: string;
  SubscriptionType: string;
}

interface StatisticsData {
  totalPolicies: number;
  byPlatform: Record<string, number>;
  byStatus: Record<string, number>;
  customized: number;
  driftCount: number;
}

interface DriftData {
  totalDrift: number;
  customizedCount: number;
  outdatedCount: number;
  customizedPolicies: any[];
  outdatedPolicies: any[];
}

const Policies: React.FC = () => {
  const [policies, setPolicies] = useState<PolicyData[]>([]);
  const [filteredPolicies, setFilteredPolicies] = useState<PolicyData[]>([]);
  const [selectedPolicies, setSelectedPolicies] = useState<number[]>([]);
  const [statistics, setStatistics] = useState<StatisticsData>({
    totalPolicies: 0,
    byPlatform: {},
    byStatus: {},
    customized: 0,
    driftCount: 0
  });
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [driftFilter, setDriftFilter] = useState('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [syncLoading, setSyncLoading] = useState(false);
  const [bulkSyncLoading, setBulkSyncLoading] = useState(false);
  
  // Dialogs
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState<PolicyData | null>(null);
  const [comparisonData, setComparisonData] = useState<any>(null);
  
  // Drift detection
  const [driftData, setDriftData] = useState<DriftData | null>(null);
  const [driftDialogOpen, setDriftDialogOpen] = useState(false);
  
  // Alerts
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('info');

  useEffect(() => {
    loadPolicies();
  }, []);

  useEffect(() => {
    filterPolicies();
  }, [policies, searchTerm, platformFilter, statusFilter, customerFilter, driftFilter]);

  const loadPolicies = async () => {
    try {
      setLoading(true);
      
      const response = await api.get('/TCAdminPolicies?limit=1000');

      setPolicies(response.data.policies);
      calculateStatistics(response.data.policies);
    } catch (error) {
      console.error('Error loading policies:', error);
      showAlert('Failed to load policies', 'error');
    } finally {
      setLoading(false);
    }
  };

  const calculateStatistics = (policiesData: PolicyData[]) => {
    const byPlatform: Record<string, number> = {};
    const byStatus: Record<string, number> = {};
    let customized = 0;

    policiesData.forEach(policy => {
      // Count by platform
      byPlatform[policy.Platform] = (byPlatform[policy.Platform] || 0) + 1;
      
      // Count by status
      byStatus[policy.DeploymentStatus] = (byStatus[policy.DeploymentStatus] || 0) + 1;
      
      // Count customized
      if (policy.IsCustomized) {
        customized++;
      }
    });

    setStatistics({
      totalPolicies: policiesData.length,
      byPlatform,
      byStatus,
      customized,
      driftCount: 0 // Will be updated when drift detection runs
    });
  };

  const filterPolicies = () => {
    let filtered = [...policies];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(policy =>
        policy.CompanyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        policy.PolicyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        policy.DisplayName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Platform filter
    if (platformFilter) {
      filtered = filtered.filter(policy => policy.Platform === platformFilter);
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(policy => policy.DeploymentStatus === statusFilter);
    }

    // Customer filter
    if (customerFilter) {
      filtered = filtered.filter(policy => policy.CompanyName === customerFilter);
    }

    // Drift filter
    if (driftFilter === 'customized') {
      filtered = filtered.filter(policy => policy.IsCustomized);
    } else if (driftFilter === 'outdated') {
      // This would require comparing with catalog ModifiedDate - simplified for now
      filtered = filtered.filter(policy => !policy.IsCustomized);
    }

    setFilteredPolicies(filtered);
  };

  const handleDetectDrift = async () => {
    try {
      setLoading(true);
      
      const response = await api.get('/TCAdminPolicies/drift');

      setDriftData(response.data);
      setStatistics(prev => ({ ...prev, driftCount: response.data.totalDrift }));
      setDriftDialogOpen(true);
      showAlert(`Detected ${response.data.totalDrift} policies with drift`, 'info');
    } catch (error) {
      console.error('Error detecting drift:', error);
      showAlert('Failed to detect policy drift', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncPolicy = async (customerPolicyId: number) => {
    try {
      setSyncLoading(true);
      
      await api.post(`/TCAdminPolicies/${customerPolicyId}/sync`, {});

      showAlert('Policy marked for redeployment', 'success');
      await loadPolicies();
    } catch (error) {
      console.error('Error syncing policy:', error);
      showAlert('Failed to sync policy', 'error');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleBulkSync = async () => {
    if (selectedPolicies.length === 0) {
      showAlert('Please select policies to sync', 'warning');
      return;
    }

    if (!confirm(`Sync ${selectedPolicies.length} selected policies?`)) {
      return;
    }

    try {
      setBulkSyncLoading(true);
      
      await api.post('/TCAdminPolicies/bulk-sync', { customerPolicyIds: selectedPolicies });

      showAlert(`${selectedPolicies.length} policies marked for redeployment`, 'success');
      setSelectedPolicies([]);
      await loadPolicies();
    } catch (error) {
      console.error('Error bulk syncing policies:', error);
      showAlert('Failed to bulk sync policies', 'error');
    } finally {
      setBulkSyncLoading(false);
    }
  };

  const handleComparePolicy = async (policy: PolicyData) => {
    try {
      setSelectedPolicy(policy);
      
      const response = await api.get(`/TCAdminPolicies/${policy.CustomerPolicyID}/compare`);

      setComparisonData(response.data);
      setCompareDialogOpen(true);
    } catch (error) {
      console.error('Error comparing policy:', error);
      showAlert('Failed to compare policy', 'error');
    }
  };

  const handleSelectAll = () => {
    const allIds = filteredPolicies.map(p => p.CustomerPolicyID);
    setSelectedPolicies(allIds);
  };

  const handleClearSelection = () => {
    setSelectedPolicies([]);
  };

  const handleToggleSelect = (customerPolicyId: number) => {
    if (selectedPolicies.includes(customerPolicyId)) {
      setSelectedPolicies(selectedPolicies.filter(id => id !== customerPolicyId));
    } else {
      setSelectedPolicies([...selectedPolicies, customerPolicyId]);
    }
  };

  const showAlert = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setTimeout(() => setAlertMessage(''), 5000);
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'Windows': return <WindowsIcon />;
      case 'macOS': return <AppleIcon />;
      case 'Android': return <AndroidIcon />;
      case 'iOS': return <IOSIcon />;
      default: return <PolicyIcon />;
    }
  };

  const getPlatformColor = (platform: string): "primary" | "secondary" | "success" | "info" => {
    switch (platform) {
      case 'Windows': return 'primary';
      case 'macOS': return 'secondary';
      case 'Android': return 'success';
      case 'iOS': return 'info';
      default: return 'primary';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Deployed': return <CheckCircleIcon />;
      case 'Failed': return <ErrorIcon />;
      case 'Pending': return <PendingIcon />;
      default: return <WarningIcon />;
    }
  };

  const getStatusColor = (status: string): "success" | "error" | "warning" | "default" => {
    switch (status) {
      case 'Deployed': return 'success';
      case 'Failed': return 'error';
      case 'Pending': return 'warning';
      default: return 'default';
    }
  };

  const uniqueCompanies = Array.from(new Set(policies.map(p => p.CompanyName))).sort();

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PolicyIcon fontSize="large" />
        Policy Management
      </Typography>

      {alertMessage && (
        <Alert severity={alertSeverity} onClose={() => setAlertMessage('')} sx={{ mb: 2 }}>
          {alertMessage}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Policies
              </Typography>
              <Typography variant="h4">
                {statistics.totalPolicies}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                By Platform
              </Typography>
              <Box>
                {Object.entries(statistics.byPlatform).map(([platform, count]) => (
                  <Chip
                    key={platform}
                    icon={getPlatformIcon(platform)}
                    label={`${platform}: ${count}`}
                    size="small"
                    color={getPlatformColor(platform)}
                    sx={{ mr: 0.5, mb: 0.5 }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Customized
              </Typography>
              <Typography variant="h4">
                {statistics.customized}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Drift Detected
              </Typography>
              <Typography variant="h4" color={statistics.driftCount > 0 ? 'warning.main' : 'text.primary'}>
                {statistics.driftCount}
              </Typography>
              <Button
                size="small"
                startIcon={<WarningIcon />}
                onClick={handleDetectDrift}
                disabled={loading}
              >
                Detect Drift
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Bulk Actions */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Customer</InputLabel>
              <Select
                value={customerFilter}
                onChange={(e) => setCustomerFilter(e.target.value)}
                label="Customer"
              >
                <MenuItem value="">All Customers</MenuItem>
                {uniqueCompanies.map(company => (
                  <MenuItem key={company} value={company}>{company}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Platform</InputLabel>
              <Select
                value={platformFilter}
                onChange={(e) => setPlatformFilter(e.target.value)}
                label="Platform"
              >
                <MenuItem value="">All Platforms</MenuItem>
                <MenuItem value="Windows">Windows</MenuItem>
                <MenuItem value="macOS">macOS</MenuItem>
                <MenuItem value="Android">Android</MenuItem>
                <MenuItem value="iOS">iOS</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="Deployed">Deployed</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Failed">Failed</MenuItem>
                <MenuItem value="Modified">Modified</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Drift</InputLabel>
              <Select
                value={driftFilter}
                onChange={(e) => setDriftFilter(e.target.value)}
                label="Drift"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="customized">Customized Only</MenuItem>
                <MenuItem value="outdated">Outdated Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={1}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setSearchTerm('');
                setPlatformFilter('');
                setStatusFilter('');
                setCustomerFilter('');
                setDriftFilter('');
              }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>

        {/* Bulk Actions Toolbar */}
        {selectedPolicies.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="body2">
              {selectedPolicies.length} selected
            </Typography>
            <Button
              variant="contained"
              startIcon={<SyncIcon />}
              onClick={handleBulkSync}
              disabled={bulkSyncLoading}
            >
              Bulk Sync
            </Button>
            <Button
              startIcon={<SelectAllIcon />}
              onClick={handleSelectAll}
            >
              Select All
            </Button>
            <Button
              startIcon={<ClearSelectionIcon />}
              onClick={handleClearSelection}
            >
              Clear Selection
            </Button>
          </Box>
        )}
      </Paper>

      {/* Policies Table */}
      <TableContainer component={Paper}>
        {loading && <LinearProgress />}
        
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedPolicies.length > 0 && selectedPolicies.length < filteredPolicies.length}
                  checked={filteredPolicies.length > 0 && selectedPolicies.length === filteredPolicies.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      handleSelectAll();
                    } else {
                      handleClearSelection();
                    }
                  }}
                />
              </TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Policy Name</TableCell>
              <TableCell>Platform</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Deployed Date</TableCell>
              <TableCell>Customized</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPolicies
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((policy) => (
                <TableRow key={policy.CustomerPolicyID} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedPolicies.includes(policy.CustomerPolicyID)}
                      onChange={() => handleToggleSelect(policy.CustomerPolicyID)}
                    />
                  </TableCell>
                  <TableCell>{policy.CompanyName}</TableCell>
                  <TableCell>{policy.DisplayName}</TableCell>
                  <TableCell>
                    <Chip
                      icon={getPlatformIcon(policy.Platform)}
                      label={policy.Platform}
                      size="small"
                      color={getPlatformColor(policy.Platform)}
                    />
                  </TableCell>
                  <TableCell>{policy.PolicyType}</TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(policy.DeploymentStatus)}
                      label={policy.DeploymentStatus}
                      size="small"
                      color={getStatusColor(policy.DeploymentStatus)}
                    />
                  </TableCell>
                  <TableCell>
                    {policy.DeployedDate ? new Date(policy.DeployedDate).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {policy.IsCustomized && (
                      <Chip label="Customized" size="small" color="warning" />
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleComparePolicy(policy)}
                      title="Compare with template"
                    >
                      <CompareIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleSyncPolicy(policy.CustomerPolicyID)}
                      disabled={syncLoading}
                      title="Force sync"
                    >
                      <SyncIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={filteredPolicies.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      {/* Drift Detection Dialog */}
      <Dialog
        open={driftDialogOpen}
        onClose={() => setDriftDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="warning" />
            Policy Drift Detection Results
          </Box>
        </DialogTitle>
        <DialogContent>
          {driftData && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={4}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Total Drift
                      </Typography>
                      <Typography variant="h4">
                        {driftData.totalDrift}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={4}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Customized
                      </Typography>
                      <Typography variant="h4">
                        {driftData.customizedCount}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={4}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Outdated
                      </Typography>
                      <Typography variant="h4">
                        {driftData.outdatedCount}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Customized Policies ({driftData.customizedCount})</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Company</TableCell>
                          <TableCell>Policy</TableCell>
                          <TableCell>Platform</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {driftData.customizedPolicies.map((policy: any) => (
                          <TableRow key={policy.CustomerPolicyID}>
                            <TableCell>{policy.CompanyName}</TableCell>
                            <TableCell>{policy.PolicyName}</TableCell>
                            <TableCell>
                              <Chip
                                icon={getPlatformIcon(policy.Platform)}
                                label={policy.Platform}
                                size="small"
                                color={getPlatformColor(policy.Platform)}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Outdated Policies ({driftData.outdatedCount})</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Company</TableCell>
                          <TableCell>Policy</TableCell>
                          <TableCell>Platform</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {driftData.outdatedPolicies.map((policy: any) => (
                          <TableRow key={policy.CustomerPolicyID}>
                            <TableCell>{policy.CompanyName}</TableCell>
                            <TableCell>{policy.PolicyName}</TableCell>
                            <TableCell>
                              <Chip
                                icon={getPlatformIcon(policy.Platform)}
                                label={policy.Platform}
                                size="small"
                                color={getPlatformColor(policy.Platform)}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDriftDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Policy Comparison Dialog */}
      <Dialog
        open={compareDialogOpen}
        onClose={() => setCompareDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CompareIcon />
            Policy Comparison: {selectedPolicy?.DisplayName}
          </Box>
        </DialogTitle>
        <DialogContent>
          {comparisonData && (
            <Box>
              <Alert severity={comparisonData.isDrift ? 'warning' : 'success'} sx={{ mb: 2 }}>
                {comparisonData.isDrift
                  ? `Policy has drift: ${comparisonData.isCustomized ? 'Customized' : 'Outdated'}`
                  : 'Policy matches catalog template'}
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Deployed Policy
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Company: {comparisonData.deployedPolicy.CompanyName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Deployed: {new Date(comparisonData.deployedPolicy.DeployedDate).toLocaleString()}
                    </Typography>
                    {comparisonData.deployedPolicy.IsCustomized && (
                      <Chip label="Customized" size="small" color="warning" sx={{ mt: 1 }} />
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Catalog Template
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Platform: {comparisonData.catalogPolicy.Platform}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Type: {comparisonData.catalogPolicy.PolicyType}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Modified: {new Date(comparisonData.catalogPolicy.ModifiedDate).toLocaleString()}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {comparisonData?.isDrift && (
            <Button
              variant="contained"
              startIcon={<SyncIcon />}
              onClick={() => {
                if (selectedPolicy) {
                  handleSyncPolicy(selectedPolicy.CustomerPolicyID);
                  setCompareDialogOpen(false);
                }
              }}
            >
              Restore to Template
            </Button>
          )}
          <Button onClick={() => setCompareDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Policies;
